/**
 * Per-bot strategy reference card content for the /bot-trades modal.
 *
 * Keyed by bot name (`agent-emacomp`, etc.) AND by strategy tag as it
 * appears in trades.csv (`EMA_COMP`, `INSIDER_CLUSTER`, ...) so the
 * lookup works regardless of whether the controller's STRATEGY_BOT
 * map has been extended for newer agents.
 *
 * Pulled from each agent's config.py + .claude/memory/project_*
 * deploy notes + scanner/RUNBOOK.md. When numbers in config.py change,
 * update this file in the same turn.
 */

const EMA_COMP = {
  bot: "agent-emacomp",
  strategy_tag: "EMA_COMP",
  title: "EMA Compression Breakout",
  one_liner: "Detects EMA 20/50/100/200 convergence, fires on vol-confirmed breakout in a non-falling long-trend.",
  timeframe: "Daily — swing (up to 40-bar hold)",
  universe: "$5B+ market cap",
  account: "Free-roamer (auto-picks paper-1/2/3 by max BP; sticky while open)",
  entry: "Next-day open after compression bar + vol spike",
  stop: "Entry − 2 × natural risk (2× the bar-low risk)",
  target: "Entry + 15 × natural risk (15R right-tail capture)",
  exit_paths: [
    "Bracket TARGET hit (15R take-profit)",
    "Bracket STOP hit (−2R stop-loss)",
    "40-trading-day hold cap → market close",
  ],
  filters: [
    "EMA 20/50/100/200 spread on bar i−1 ≤ 5% of close (COMPRESS_PCT = 0.05)",
    "Bar i volume ≥ 1.2× SMA20(volume) (VOL_MULT = 1.2)",
    "EMA200 non-falling over 20 bars (TREND_LOOKBACK = 20)",
    "natural_risk_pct in [0.2%, 5%] — skip skinny stops + too-wide stops",
  ],
  risk: "1% account-risk per trade, $10k notional cap, cash-only (no leverage)",
  backtest: {
    window: "4-year (2023–2026 YTD), 905-ticker survivorship-free flat-files",
    win_rate: "33–47% by year (~42% avg)",
    expectancy: "−0.18R to +2.00R by year",
    profit_factor: "0.87–3.51 by year (~1.65 avg)",
    cagr: "~+34.6% (4yr at 1% risk, no concurrency cap)",
    headline: "Path A: ~+34.6% CAGR over 4yr at 1% risk, no concurrency cap. Accept ~74% max DD for max return. 2025 was a losing year (choppy regime broke long-trend alignment).",
  },
  why_deployed: [
    "Survives 4yr survivorship-free OOS — not a 2026 hot-streak artifact",
    "target=15R is the right-tail-capture sweet spot; lower targets cap winners, higher targets time out",
    "COMPRESS_PCT=0.05 validated against tighter values (0.01 → 0% win rate)",
    "Hand-tuned filter outperformed sklearn HGB ML ranker on the same features",
  ],
  references: [
    "backtest/ema-compression/2026-05-11-ema-compression-50b.md",
    ".claude/memory/project_emacomp_compress_pct_validated.md",
    ".claude/memory/feedback_ml_on_emacomp_smoothing.md",
  ],
};

const PIPE_BOTTOM = {
  bot: "agent-pipe",
  strategy_tag: "PIPE_BOTTOM",
  title: "PIPE Bottom Pattern",
  one_liner: "Weekly-aggregated bottoming pattern → multi-day breakout swing.",
  timeframe: "Daily aggregated to weekly — swing",
  universe: "$5B+ market cap",
  account: "Free-roamer (auto-picks paper-1/2/3 by max BP; sticky while open)",
  entry: "Next-day open after PIPE_BOTTOM weekly signal",
  stop: "Per-signal natural-risk stop",
  target: "Entry + 4 × natural risk (4R)",
  exit_paths: [
    "Bracket TARGET hit (4R)",
    "Bracket STOP hit",
    "30-trading-day hold cap → market close",
  ],
  filters: [
    "Weekly PIPE pattern: deep wick + recovery",
    "$5B+ universe (liquidity floor)",
  ],
  risk: "2.5% nominal (capped by $10k notional in shared accounts)",
  backtest: {
    window: "3.79yr realistic-execution sweep, 100 FLOATING signals (2026-05-08)",
    win_rate: "~60% at 4R target",
    expectancy: "+1.01R mean (~+1.0R after slippage)",
    profit_factor: "—",
    cagr: "— (sweep reports P50 $2k→$6.3k over 24mo at 2.5% risk, not annualized)",
    headline: "FLOATING-only 4R is the only chart-pattern strategy with unambiguous post-realism edge. 24-mo P50 on $2k start = $6.3k at 2.5% risk; median MDD ~15%, P95 ~27%.",
  },
  why_deployed: [
    "Targets the right-tail of weekly mean-reversion setups",
    "Free-roamer — auto-picks paper-1/2/3 by max BP, sticky while positions open",
  ],
  references: [
    "scanner/agent-pipe/README.md",
  ],
};

const GOLDEN_CROSS = {
  bot: "agent-goldencross",
  strategy_tag: "GOLDEN_CROSS",
  title: "EMA 30/50 Cross + ATR Compression",
  one_liner: "Classic golden cross + vol + ATR-percentile filter — the export-worthy backtest finding of 2026-05.",
  timeframe: "Daily — long-horizon swing",
  universe: "$5B+ market cap",
  account: "Free-roamer (auto-picks paper-1/2/3 by max BP; sticky while open)",
  entry: "Next-day open after EMA30 crosses above EMA50",
  stop: "Entry − 3 × natural risk (3× ATR-derived)",
  target: "Entry + 12 × natural risk (12R)",
  exit_paths: [
    "Bracket TARGET hit (12R)",
    "Bracket STOP hit (−3R)",
    "200-trading-day hold cap → market close",
  ],
  filters: [
    "EMA30 crosses above EMA50 on bar i",
    "vol[i] ≥ 2 × SMA20(volume)",
    "close[i] > EMA200 (uptrend confirmation)",
    "ATR(20)/close in bottom 30% of trailing 252 bars",
    "Earnings buffer ±2 days",
  ],
  risk: "1.5% per trade, max 4 concurrent positions (6% portfolio risk worst-case)",
  backtest: {
    window: "6-year IS + walk-forward OOS (n=19 trades)",
    win_rate: "36.8% (OOS aggregate; 16.2% IS / 44.6% profitable IS)",
    expectancy: "+1.71R OOS (vs +4.36R IS headline)",
    profit_factor: "1.89 OOS (vs 3.28 IS)",
    cagr: "~+22% OOS at 1.5% risk (MC sim P50 = +23%; ~+15% at 1% risk)",
    headline: "ATR-percentile filter halved MDD (16.6R) and produced 0/4 negative WF folds. ~22% CAGR at 1.5% risk over test period.",
  },
  why_deployed: [
    "WF-audit confirmed +1.71R OOS exp / 0 negative folds",
    "ATR-percentile is the discovery — without it the classic 50/200 SMA loses money",
    "Active exit management (BE move, chandelier, MA trail, partial scale) all reduced edge vs static 12R target",
  ],
  kill_switch: "rolling-30 expectancy < +0.5R",
  references: [
    ".claude/memory/project_golden_cross_winner.md",
    ".claude/memory/project_agent_goldencross_deployed_2026_05_31.md",
  ],
};

const INSIDER_CLUSTER = {
  bot: "agent-insider",
  strategy_tag: "INSIDER_CLUSTER",
  title: "Form 4 Insider Cluster + Wyckoff ATR",
  one_liner: "Two-or-more simultaneous insider buys filtered by tight ATR compression.",
  timeframe: "EOD scan 16:15 ET — 5-day timed swing",
  universe: "$5B+ market cap",
  account: "Free-roamer (auto-picks paper-1/2/3 by max BP; sticky while open)",
  entry: "Next-day open after cluster signal (plain market BUY, no bracket)",
  stop: "NONE — risk capped by holding period only",
  target: "NONE — timed exit",
  exit_paths: [
    "T+5 trading days → market close at the close",
  ],
  filters: [
    "Cluster size ≥ 2 (two or more insiders buy within window)",
    "ATR(5)/ATR(20) ≤ 0.85 (Wyckoff-style compression — the entire lever)",
    "Optional: $≥5M filed value (sub-cell with highest hit rate)",
  ],
  risk: "2% nominal per trade (capped by notional)",
  backtest: {
    window: "194 cluster events (MVP) + 30 events with ATR overlay (Phase 2)",
    win_rate: "77% (size=2 + ATR≤0.85); 86% (size=2 + ATR + $≥5M)",
    expectancy: "+3.52% / 5d (size=2 + ATR); +6.56% / 5d (size=2 + ATR + $≥5M)",
    profit_factor: "—",
    cagr: "— (Phase 2 reports per-5d %-edge; ~15 signals/yr, not natively CAGR-framed)",
    headline: "ATR compression overlay excludes 84% of worst-tail losers. Counter-intuitive: bigger clusters underperformed.",
  },
  why_deployed: [
    "Phase 2 ATR overlay was the entire lever — without it the edge is +1.17%/5d",
    "Universe-disjoint from CAP family — safe co-tenant on paper-3",
  ],
  references: [
    ".claude/memory/project_form4_cluster_mvp_2026_05_18.md",
    ".claude/memory/project_form4_cluster_phase2_wyckoff.md",
  ],
};

const SPY_PRIMED = {
  bot: "agent-spy-primed-am",
  strategy_tag: "SPY_PRIMED",
  title: "SPY-Primed Megacap Intraday (Alt-A)",
  one_liner: "When SPY opens hot, buy the megacaps that LAGGED in the first 15 minutes.",
  timeframe: "Intraday — entry 09:45 ET, flatten 15:55 ET",
  universe: "Top-200 megacap by market cap",
  account: "Free-roamer (auto-picks paper-1/2/3 by max BP; sticky while open)",
  entry: "09:45 ET market BUY when ALL trigger conditions fire",
  stop: "NONE — intraday only, flatten at 15:55 ET",
  target: "NONE — flatten at 15:55 ET",
  exit_paths: [
    "Intraday flatten at 15:55 ET (no overnight)",
  ],
  filters: [
    "SPY first15 return ≥ +0.20% (trigger gate)",
    "EMA stack: 20 > 50 > 200 (uptrend)",
    "RS_20d in top tercile (relative strength leader)",
    "First15 return in bottom tercile (laggard within the leader cohort — the 'catch-up' edge)",
    "Distance from 52wk high in top tercile (room to run)",
    "Frozen tercile cutpoints from the last 13-wk trigger window",
  ],
  risk: "1% per pick, max 4 picks per trigger day",
  backtest: {
    window: "18-fold rolling walk-forward (13wk-train / 4wk-test / 4wk-step) on 24mo 200-megacap 5-min cache",
    win_rate: "Strict Alt-A (deployed): 38.5% of picks beat SPY by ≥50bps half-day vs 30.19% universe baseline = +8.35pp lift (~8 extra winners per 100 picks). Aggregate Alt-A: +6.18pp lift.",
    expectancy: "+0.106% mean alpha per pick (half-day vs SPY) — small per-trade edge by design",
    profit_factor: "—",
    cagr: "— (rare trigger: ~25 days/yr × ≤4 picks ≈ ~100 picks/yr × +0.106% ≈ ~10bps annual contribution to deployed slice)",
    headline: "Strict combo earned positive mean alpha in 71% of OOS test windows (12 of 17 folds). Small per-trade edge but the most methodologically rigorous backtest in the repo — exogenous SPY trigger, all features pre-09:45, walk-forward from step zero. The discipline that killed prior intraday-hunt false positives.",
  },
  why_deployed: [
    "SMALL EDGE, well-validated. +0.106%/pick is thin — but the methodology bar was the gate, not the magnitude",
    "Prior 5-min PRC intraday-hunt looked +0.35 ATR IS, failed 0/3 OOS windows. This passed 12/17 — methodology beats magnitude",
    "Exogenous SPY trigger (regime gate, not universe-mined) — lower overfit surface than data-derived signals",
    "Cheap downside: no overnight risk, no stop, hard 15:55 flatten, auto kill-switch on rolling-30 alpha < 0%",
    "Rare trigger (~25 days/yr × 4 picks) bounds total exposure by construction",
  ],
  kill_switch: "rolling-30 average alpha < 0%",
  references: [
    ".claude/memory/project_spy_primed_stocks_2026_05_29.md",
    ".claude/memory/project_agent_spy_primed_am_deployed_2026_05_31.md",
    ".claude/memory/feedback_intraday_hunt_walkforward.md",
  ],
};

const CAP_BASE = {
  one_liner: "5-minute intraday DOWN-bar fade on below-average volume.",
  timeframe: "Intraday — 5-min bars, flatten 15:55 ET",
  universe: "$5B+ market cap",
  entry: "Buy on the close of a 5-min DOWN bar that meets drop% + vol% gates",
  exit_paths: [
    "Bracket TARGET hit (intraday)",
    "Bracket STOP hit (−0.7%)",
    "Intraday flatten at 15:55 ET",
  ],
  filters: [
    "CAP_A: drop ≥ 2.5%, vol ratio < 1× SMA20, between 10:00 and 11:00 ET",
    "CAP_B: drop ≥ 1.75%, vol ratio < 0.5×, between 10:00 and 11:00 ET",
    "CAP_C: drop ≥ 1.75%, vol ratio < 0.5×, between 11:00 and 12:00 ET",
    "Daily loss limit 3%; PDT day-trade limit (3 trades/5 days) below $25k equity",
  ],
  risk: "2% nominal per trade, $10k notional cap (primary risk control)",
};

const CAP_2to1 = {
  ...CAP_BASE,
  bot: "agent",
  strategy_tag: "CAP_A",   // primary tag; also appears as CAP_B/CAP_C
  title: "CAP A/B/C — 2:1 R/R (baseline)",
  account: "paper-1 (pinned)",
  stop: "−0.7%",
  target: "+1.4% (2:1)",
  backtest: {
    window: "2-year +25s arrival-lag validation (2026-05-08), $25k start",
    win_rate: "~45–55% (highest WR of the CAP family — tightest target)",
    expectancy: "Lower per-trade R than 3:1 / 5:1 (2R cap on winners)",
    profit_factor: "—",
    cagr: "— (2yr validation memo reports 3:1 / 4:1 / 5:1 explicitly; 2:1 baseline CAGR not directly stated)",
    headline: "Baseline of the CAP family. Faster wins, lower per-trade EV than 3:1/5:1. A/B anchor against the wider-target variants.",
  },
  why_deployed: [
    "Anchors the CAP-family A/B-test against the 3:1 and 5:1 variants",
  ],
};

const CAP_3to1 = {
  ...CAP_BASE,
  bot: "agent-3to1",
  strategy_tag: "CAP_A",
  title: "CAP A/B/C — 3:1 R/R",
  account: "paper-2 (pinned)",
  stop: "−0.7%",
  target: "+2.1% (3:1)",
  backtest: {
    window: "2-year +25s arrival-lag validation (2026-05-08), $25k start",
    win_rate: "~38% Y1; ~12pp lower Y2 (regime-sensitive)",
    expectancy: "CAGR +18.5% over 2yr (vs original +169% inflated sim)",
    profit_factor: "—",
    cagr: "+18.5% (2yr, $25k start, +25s arrival lag); worst-year MDD 14.7%, Calmar 1.26",
    headline: "Worst-year MDD 14.7%. Walk-forward passed overfit check (TEST > TRAIN). Methodology corrections account for the 9× drop from the original sim.",
  },
  why_deployed: [
    "Validated against 2-year realistic execution model",
    "Counter-intuitive: lower fill latency reduced edge (capitulation continues 10–25s past trigger close)",
  ],
  references: [
    ".claude/memory/project_agent_3to1_2yr_validation.md",
  ],
};

const CAP_5to1 = {
  ...CAP_BASE,
  bot: "agent-5to1",
  strategy_tag: "CAP_A",
  title: "CAP A/B/C — 5:1 R/R (best Calmar)",
  account: "paper-3 (pinned)",
  stop: "−0.7%",
  target: "+3.5% (5:1)",
  backtest: {
    window: "2-year +25s arrival-lag validation (2026-05-08), $25k start",
    win_rate: "~27% (lower WR offset by bigger winners)",
    expectancy: "CAGR +35.1% over 2yr — best Calmar of the CAP family",
    profit_factor: "—",
    cagr: "+35.1% (2yr, $25k start, +25s arrival lag); worst-year MDD 14.9% — best Calmar of CAP family",
    headline: "Worst-year MDD 14.9%. Stretching target 2.1%→3.5% at the same 0.7% stop captures more post-bounce continuation. Deployed 2026-05-08 paper-only as A/B vs 3:1.",
  },
  why_deployed: [
    "Best Calmar in the 2-year validation — favored on a risk-adjusted basis",
    "Wider target captures more of the right tail",
  ],
  references: [
    ".claude/memory/project_agent_3to1_2yr_validation.md",
  ],
};

const MID_FADE = {
  bot: "agent-midcap",
  strategy_tag: "MID_FADE",
  title: "Mid-cap 5-min Fade — 3:1 R/R",
  one_liner: "Same fade idea as CAP family, scaled to the $1B–$5B mid-cap band.",
  timeframe: "Intraday — 5-min bars, flatten 15:55 ET",
  universe: "$1B–$5B market cap (disjoint from CAP family's $5B+)",
  account: "Free-roamer (auto-picks paper-1/2/3 by max BP; sticky while open)",
  entry: "Buy on close of a 5-min DOWN bar that meets MID-cap drop% + vol% gates",
  stop: "−1.0%",
  target: "+3.0% (3:1)",
  exit_paths: [
    "Bracket TARGET hit (intraday)",
    "Bracket STOP hit",
    "Intraday flatten at 15:55 ET",
  ],
  filters: [
    "Mid-cap universe gate ($1B ≤ mkt cap < $5B)",
    "Daily loss limit 3%; PDT day-trade limit below $25k equity",
  ],
  risk: "2% nominal per trade, notional-capped",
  backtest: {
    window: "12-mo, 1,143 mid-cap tickers, n=184 trades (pre $10M $-ADV filter added 2026-05-27)",
    win_rate: "72.3% backtested (forward expect 65–72% post-filter)",
    expectancy: "+1.90% / trade pre-slip; ~+1.70% after 10bps round-trip",
    profit_factor: "—",
    cagr: "— (12-mo backtest only; revalidate after first 30 post-$-ADV-filter trades)",
    headline: "Mid-cap variant of CAP_A; ~3.5 trades/week, ~184/yr. Universe-disjoint from CAP family so co-tenants on any paper account are safe. Revalidate after first 30 post-$-ADV-filter trades.",
  },
  why_deployed: [
    "Universe-disjoint from $5B+ CAP agents — no same-ticker contention",
  ],
};

// Lookup is keyed by BOTH bot name and strategy tag for resilience to
// changes in the controller's STRATEGY_BOT map. The bot-name keys are
// authoritative; strategy-tag keys point to the same object as a fallback.
export const STRATEGY_META = {
  "agent-emacomp": EMA_COMP,
  "EMA_COMP": EMA_COMP,

  "agent-pipe": PIPE_BOTTOM,
  "PIPE_BOTTOM": PIPE_BOTTOM,

  "agent-goldencross": GOLDEN_CROSS,
  "GOLDEN_CROSS": GOLDEN_CROSS,

  "agent-insider": INSIDER_CLUSTER,
  "INSIDER_CLUSTER": INSIDER_CLUSTER,
  "INSIDER": INSIDER_CLUSTER,

  "agent-spy-primed-am": SPY_PRIMED,
  "SPY_PRIMED": SPY_PRIMED,
  "SPY_PRIMED_AM": SPY_PRIMED,

  "agent": CAP_2to1,
  "agent-3to1": CAP_3to1,
  "agent-5to1": CAP_5to1,
  "agent-midcap": MID_FADE,

  // CAP / CAP_A/B/C are shared across the three R/R variants; resolving a
  // strategy-only key returns the baseline 2:1 card and the user can
  // pick the right variant from the bot column. Production trades.csv
  // tags rows as bare `CAP`; CAP_A/B/C are kept for legacy/future use.
  "CAP": CAP_2to1,
  "CAP_A": CAP_2to1,
  "CAP_B": CAP_2to1,
  "CAP_C": CAP_2to1,
  "MID_FADE": MID_FADE,
};

/**
 * Resolve a meta entry for either a bot name or a strategy tag.
 * Returns null if neither key matches — caller should handle gracefully.
 */
export function lookupStrategyMeta({ bot, strategy }) {
  if (bot && STRATEGY_META[bot]) return STRATEGY_META[bot];
  if (strategy && STRATEGY_META[strategy]) return STRATEGY_META[strategy];
  return null;
}
